// src/pages/CreateMediaKitPage.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, FileText, Loader2 } from 'lucide-react';
import { mediaKitApi } from '../services/mediaKitApi';

export default function CreateMediaKitPage({ myAmazonStorefrontUrl }) {
    const navigate = useNavigate();
    const [mode, setMode] = useState(null); // 'blank' | 'import'
    const [storefrontUrl, setStorefrontUrl] = useState(myAmazonStorefrontUrl || '');
    const [slug, setSlug] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleContinue = async () => {
        if (slug.trim().length < 3) { setError('Choose a URL of at least 3 characters.'); return; }
        setLoading(true);
        setError(null);
        try {
            let prefill = {};
            if (mode === 'import' && storefrontUrl.trim()) {
                try { prefill = await mediaKitApi.importStorefront(storefrontUrl.trim()); }
                catch { /* fall back to blank silently if scrape fails */ }
            }
            const { kit } = await mediaKitApi.create({ slug: slug.trim(), ...prefill });
            navigate(`/aip/media-kit/${kit.id}`);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-8">
                <h1 className="text-xl font-bold text-slate-800 mb-1">Create a media kit</h1>
                <p className="text-sm text-slate-500 mb-6">Start blank, or pull your info from your Amazon Storefront.</p>

                <div className="grid grid-cols-2 gap-3 mb-6">
                    <button onClick={() => setMode('blank')}
                        className={`p-4 rounded-xl border-2 text-left transition ${mode === 'blank' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}>
                        <FileText size={18} className="mb-2 text-slate-500" />
                        <p className="text-sm font-bold text-slate-800">Start blank</p>
                        <p className="text-xs text-slate-400">Build it from scratch</p>
                    </button>
                    <button onClick={() => setMode('import')}
                        className={`p-4 rounded-xl border-2 text-left transition ${mode === 'import' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}>
                        <Sparkles size={18} className="mb-2 text-slate-500" />
                        <p className="text-sm font-bold text-slate-800">Copy my Storefront</p>
                        <p className="text-xs text-slate-400">Prefill name, bio, socials</p>
                    </button>
                </div>

                {mode === 'import' && (
                    <input
                        className="w-full mb-4 px-3 py-2 rounded-lg border border-slate-200 text-sm"
                        placeholder="https://www.amazon.com/shop/your-handle"
                        value={storefrontUrl}
                        onChange={(e) => setStorefrontUrl(e.target.value)}
                    />
                )}

                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Your kit's URL</label>
                <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm text-slate-400">/kit/</span>
                    <input
                        className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm"
                        value={slug}
                        onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                        placeholder="your-name"
                    />
                </div>
                {error && <p className="text-xs text-red-500 mb-3">{error}</p>}

                <button
                    onClick={handleContinue}
                    disabled={!mode || loading}
                    className="w-full mt-2 py-2.5 rounded-lg bg-blue-600 text-white font-bold text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2"
                >
                    {loading ? <Loader2 size={16} className="animate-spin" /> : null} Continue
                </button>
            </div>
        </div>
    );
}